import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {string | null} */
  #savedViewMode = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    document.addEventListener('facets:before-render', this.#handleBeforeRender);
    this.setAttribute('initialized', '');

    // Intercept section renders to preserve view state
    this.#interceptSectionRender();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener('facets:before-render', this.#handleBeforeRender);
  }

  /**
   * Handles the before-render event from facets
   * @param {CustomEvent} event - The custom event with section ID
   */
  #handleBeforeRender = (event) => {
    // Save the current view mode before the section re-renders
    this.#saveCurrentViewMode();
  };

  /**
   * Intercepts section rendering to preserve the current view mode
   */
  #interceptSectionRender() {
    // Use MutationObserver to detect when the section re-renders
    const observer = new MutationObserver(() => {
      // If we had a saved view mode, restore it after the morph
      if (this.#savedViewMode) {
        this.#restoreViewAfterRender(this.#savedViewMode);
        this.#savedViewMode = null;
      }
    });

    // Observe changes to the product grid container
    observer.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Gets the current view mode from the grid
   * @returns {string} The current view mode ('columnas' or 'editorial')
   */
  #getCurrentViewMode() {
    const { grid } = this.refs;
    if (!grid) return 'columnas';

    return grid.getAttribute('product-grid-view') || 'columnas';
  }

  /**
   * Saves the current view mode before a section re-render
   */
  #saveCurrentViewMode() {
    this.#savedViewMode = this.#getCurrentViewMode();
  }

  /**
   * Restores the view mode after a section re-render
   * @param {string} viewMode - The view mode to restore
   */
  #restoreViewAfterRender(viewMode) {
    const { grid, editorialGrid } = this.refs;

    if (!grid || !editorialGrid) return;

    // Restore the view based on saved mode
    if (viewMode === 'editorial') {
      grid.style.display = 'none';
      editorialGrid.style.display = 'block';
      grid.setAttribute('product-grid-view', 'editorial');
    } else {
      grid.style.display = '';
      editorialGrid.style.display = 'none';
      grid.setAttribute('product-grid-view', 'columnas');
    }

    // Update radio button state
    const options = document.querySelectorAll(`input[type="radio"][name="grid"]`);
    for (const option of options) {
      if (option instanceof HTMLInputElement) {
        option.checked = option.value === viewMode;
      }
    }
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Sets the layout and toggles between grid views.
   *
   * @param {string} value - 'columnas' or 'editorial'
   */
  #setLayout(value) {
    const { grid, editorialGrid } = this.refs;

    if (!grid || !editorialGrid) return;

    // Toggle visibility between columnas and editorial
    if (value === 'editorial') {
      grid.style.display = 'none';
      editorialGrid.style.display = 'block';
    } else {
      grid.style.display = '';
      editorialGrid.style.display = 'none';
    }

    // Update attribute for CSS targeting
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
